import {
  EllipsedText,
  FlexColumn,
  FlexRow,
  MaterialIcon,
  StyledButton,
  useElementWidth,
} from '@gorila-shared-ui/components';
import { Panel, StatelessAccordion } from 'baseui/accordion';
import { StyledDivider } from 'baseui/divider';
import { ChevronDown } from 'baseui/icon';
import { Item, Navigation, NavigationProps } from 'baseui/side-navigation';
import { ParagraphMedium, ParagraphXSmall } from 'baseui/typography';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useHover } from 'usehooks-ts';
import { EXPANDED_SIDENAV_WIDTH, SIDENAV_WIDTH } from '../../constants/app';
import { useSidenavItems } from '../../hooks/useSidenavItems';
import { useStyles } from '../../hooks/useStyles';
import { currentPageState, sidenavIsPinnedState, sidenavSectionsState } from '../../storage/app';
import { Page } from '../../types/core';
import { usePages } from '../../hooks/usePages';

function AccordionTitle({ title }: Readonly<{ title: string }>) {
  const { theme, css } = useStyles();
  return (
    <FlexRow
      gap={theme.sizing.scale400}
      centered
      classNames={css({ paddingLeft: theme.sizing.scale200 })}
    >
      <ParagraphMedium margin={0}>{title}</ParagraphMedium>
    </FlexRow>
  );
}
export function Sidenav() {
  const { css, theme, classes, concatenateClasses } = useStyles();
  const navigate = useNavigate();
  const [sidenavIsPinned, setSidenavIsPinned] = useRecoilState(sidenavIsPinnedState);
  const currentPage = useRecoilValue(currentPageState);
  const location = useLocation();
  const [expanded, setExpanded] = useRecoilState(sidenavSectionsState);
  const { allPages, getPageIdByPanelKeyRoute, getPageRoute } = usePages();
  const { elementRef } = useElementWidth();
  const isHover = useHover(elementRef);
  const version = process.env.REACT_APP_VERSION;
  const [sideNavZIndex, setSideNavZIndex] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!sidenavIsPinned && isHover) {
      setSideNavZIndex(1);
    } else if (!sidenavIsPinned && !isHover) {
      const timeout = setTimeout(() => {
        setSideNavZIndex(undefined);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isHover, sidenavIsPinned]);

  const activeItemId = useMemo(() => {
    if (currentPage.id === 'dashboardPanels') {
      const reportPageId = getPageIdByPanelKeyRoute(location.pathname);
      if (reportPageId) return reportPageId;
    }
    return currentPage.parentId ?? currentPage.id;
  }, [currentPage, location]);

  const itemTitle = (page: Page) => {
    return page.name;
  };

  const items: Item[] = allPages.map((page) => ({
    itemId: page.id,
    title: (
      <FlexRow
        classNames={concatenateClasses(classes.centeredStart, css({ overflow: 'hidden' }))}
        gap={theme.sizing.scale400}
      >
        {page.icon && (
          <span className={css({ flexShrink: 0, lineHeight: 0 })}>
            <MaterialIcon name={page.icon} />
          </span>
        )}
        <EllipsedText inline>{itemTitle(page)}</EllipsedText>
      </FlexRow>
    ),
  }));
  const { coreItems, sections, pageSection } = useSidenavItems(items);

  useEffect(() => {
    setExpanded(!pageSection ? [] : [pageSection]);
  }, [pageSection]);

  const navigationProps: Omit<NavigationProps, 'items'> = {
    activeItemId: activeItemId,
    onChange: ({ event, item }) => {
      event.preventDefault();
      const route = getPageRoute(item.itemId ?? '');
      if (route) {
        navigate(route, { replace: false });
      }
    },
    overrides: {
      NavItem: {
        style: () => {
          return {
            paddingLeft: theme.sizing.scale750,
            paddingRight: theme.sizing.scale600,
            paddingTop: theme.sizing.scale300,
            paddingBottom: theme.sizing.scale300,
          };
        },
      },
    },
  };

  const sidenavWidth = (() => {
    if (!sidenavIsPinned && !isHover) {
      return `${SIDENAV_WIDTH}px`;
    }
    return `${EXPANDED_SIDENAV_WIDTH}px`;
  })();

  return (
    <FlexColumn
      ref={elementRef}
      gap={0}
      classNames={css({
        display: 'flex',
        backgroundColor: theme.colors.backgroundSecondary,
        width: sidenavWidth,
        position: 'fixed',
        top: '0',
        bottom: '0',
        left: '0',
        transition: 'left 0.3s ease, margin-left 0.2s ease-in-out, width 0.3s ease',
        zIndex: sideNavZIndex,
      })}
    >
      <FlexColumn
        centered
        classNames={css({
          minHeight: '66px',
          paddingRight: theme.sizing.scale500,
          paddingLeft: theme.sizing.scale500,
        })}
      >
        <img
          src={'/img/gnp_logo.png'}
          alt=""
          style={{
            height: '50px',
            objectFit: 'cover',
            maxWidth: !sidenavIsPinned && !isHover ? '50px' : undefined,
            overflow: 'hidden',
          }}
        />
      </FlexColumn>
      <FlexColumn
        gap={theme.sizing.scale400}
        classNames={css({ overflowY: 'auto', overflowX: 'hidden' })}
      >
        <Navigation
          items={coreItems}
          {...navigationProps}
        />

        {!sidenavIsPinned && (
          <>
            {sections.map((section) => (
              <Fragment key={section.id}>
                <StyledDivider style={{ width: '100%', marginTop: 0, marginBottom: 0 }} />
                <Navigation
                  items={section.items}
                  {...navigationProps}
                />
              </Fragment>
            ))}
          </>
        )}

        {sidenavIsPinned && (
          <StatelessAccordion
            expanded={expanded}
            onChange={({ key, expanded }) => {
              if (pageSection !== key) setExpanded(expanded);
            }}
            accordion={false}
            overrides={{
              Root: {
                style: {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.sizing.scale600,
                },
              },
              Header: {
                style: {
                  backgroundColor: 'transparent',
                  paddingTop: theme.sizing.scale200,
                  paddingBottom: theme.sizing.scale200,
                  paddingLeft: theme.sizing.scale700,
                  paddingRight: theme.sizing.scale600,
                },
              },
              Content: {
                style: {
                  backgroundColor: 'transparent',
                  paddingLeft: 0,
                  paddingRight: 0,
                  paddingBottom: 0,
                  paddingTop: 0,
                },
              },
              PanelContainer: {
                style: {
                  borderBottomStyle: 'none',
                },
              },
              ToggleIcon: {
                component: ({ $expanded }) => {
                  return (
                    <ChevronDown
                      title=""
                      size={theme.sizing.scale650}
                      overrides={{
                        Svg: {
                          style: {
                            transition: 'transform 0.2s ease-in-out',
                            transform: $expanded ? 'rotate(-180deg)' : undefined,
                          },
                        },
                      }}
                    />
                  );
                },
              },
            }}
          >
            {sections.map((section) => (
              <Panel
                key={section.id}
                disabled={pageSection === section.id}
                title={<AccordionTitle title={section.title} />}
              >
                <Navigation
                  items={section.items}
                  {...navigationProps}
                />
              </Panel>
            ))}
          </StatelessAccordion>
        )}
      </FlexColumn>
      <FlexRow
        centered
        classNames={css({
          padding: theme.sizing.scale500,
          marginTop: 'auto',
        })}
      >
        <StyledButton
          kind="secondary"
          onClick={() => setSidenavIsPinned((prev) => !prev)}
          full
        >
          <FlexRow centered>
            <MaterialIcon name={sidenavIsPinned ? 'keep_off' : 'keep'} />
          </FlexRow>
        </StyledButton>
      </FlexRow>
      {version && (
        <FlexRow
          centered
          classNames={css({
            paddingRight: theme.sizing.scale500,
            paddingLeft: theme.sizing.scale500,
            marginBottom: theme.sizing.scale500,
          })}
        >
          <ParagraphXSmall margin={0}>v{version}</ParagraphXSmall>
        </FlexRow>
      )}
    </FlexColumn>
  );
}
